import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { Login } from '@pages';
import { AppRoutes, HELP_PORTAL_REDIRECT } from '@consts/routes';
import { useMintPortalJwtMutation } from '@features/redux/apis/tickets';
import { hasPartnerSession } from '@utils/partnerSession';
import { useAlert } from '@hooks/useAlert';
import type { RootState } from '@features/redux/store';
import { Redirect } from '../helpers/Redirect';

/** ?redirect=helpportal (set by the standalone portal's "Sign in as Partner")
 *  sends a partner to the Help Portal after authenticating, instead of the
 *  Insights dashboard. The silent handoff only runs while the shared
 *  partner-session cookie is live (set at partner sign-in, cleared by the
 *  portal's logout) — otherwise fresh credentials are required. */
export const LoginRoute = () => {
    const idToken = useSelector((state: RootState) => state.auth.IdToken);
    const [searchParams] = useSearchParams();
    const [mintPortalJwt] = useMintPortalJwtMutation();
    const { setAlert } = useAlert();
    const [failed, setFailed] = useState(false);

    const wantsHelpPortal = searchParams.get('redirect') === HELP_PORTAL_REDIRECT;
    const partnerSession = hasPartnerSession();

    useEffect(() => {
        if (!idToken || !wantsHelpPortal || !partnerSession || failed) return;
        const mint = () => mintPortalJwt({ token: idToken }).unwrap();
        // A first login can run a slow Intercom backfill server-side, so the
        // mint may time out once and succeed on retry.
        mint()
            .catch(mint)
            .then(({ jwt, loginUrl }) => {
                window.location.href = `${loginUrl}#jwt=${encodeURIComponent(jwt)}`;
            })
            .catch(() => {
                setAlert('Could not open the Help Portal. Taking you to Insights instead.', 'error');
                setFailed(true);
            });
    }, [idToken, wantsHelpPortal, partnerSession, failed]);

    if (idToken && wantsHelpPortal && !failed) {
        // Cookie live: handing off to the portal (effect above) — render
        // nothing briefly. Cookie cleared (portal logout): ask for credentials
        // again; signing in re-marks the cookie and the handoff resumes.
        return partnerSession ? null : <Login />;
    }

    if (idToken) {
        // On handoff failure, fall back to normal dashboard behavior.
        return <Redirect to={AppRoutes.Insights} />;
    }

    return <Login />;
};
